import { userToursSeenAtom } from '@/global-store';
import { useAtom } from 'jotai';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Joyride, { ACTIONS, LIFECYCLE, STATUS } from 'react-joyride';
import { useLocation } from 'react-router-dom';

function waitForScrollEnd() {
  let lastChangedFrame = 0;
  let lastX = window.scrollX;
  let lastY = window.scrollY;

  return new Promise((resolve) => {
    function tick(frames) {
      if (frames >= 500 || frames - lastChangedFrame > 20) {
        resolve();
      } else {
        if (window.scrollX != lastX || window.scrollY != lastY) {
          lastChangedFrame = frames;
          lastX = window.scrollX;
          lastY = window.scrollY;
        }
        requestAnimationFrame(tick.bind(null, frames + 1));
      }
    }
    tick(0);
  });
}

/**
 * UserTour component to guide users through a set of steps.
 * @param {Object} props - The props object.
 * @param {Object[]} props.steps - An array of steps for the tour.
 * @param {Object} [props.joyrideProps] - Optional. Props to pass to the Joyride component.
 */
function UserTour({ /*isIntroTour,  containerRef */ steps, joyrideProps }) {
  const { t } = useTranslation();

  const location = useLocation();

  const path = /*isIntroTour ? 'INTRO_TOUR' : */ location.pathname;

  const [userToursSeen, setUserToursSeen] = useAtom(userToursSeenAtom);

  const [runJoyride, setRunJoyride] = useState(false);

  useEffect(() => {
    if (!userToursSeen?.includes(path)) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;

        setTimeout(() => {
          setRunJoyride(true);

          setTimeout(() => {
            const dialog = document.querySelector('.react-joyride__tooltip');

            if (dialog) {
              dialog.focus();
            }
          }, 25);
        }, 25);
      }, 125);
    }
  }, [userToursSeen?.length, path, userToursSeen, runJoyride]);

  return (
    <Joyride
      callback={(data) => {
        const { status, lifecycle, action /*, step */ } = data;

        if (action === ACTIONS.UPDATE && lifecycle === LIFECYCLE.TOOLTIP) {
          waitForScrollEnd().then(() => {
            window.dispatchEvent(new Event('resize'));
          });
        }

        const nextButton = document.querySelector(
          '.react-joyride__tooltip button[data-action="primary"]',
        );

        // show the steps in the aria label for the next button
        if (nextButton && nextButton.querySelector('span')?.textContent) {
          nextButton.ariaLabel = nextButton.querySelector('span').textContent;
        }

        // check if the tour has been completed by either skipping or finishing it
        if (
          (status === STATUS.FINISHED || status === STATUS.SKIPPED) &&
          lifecycle === LIFECYCLE.COMPLETE
        ) {
          document.body.style.overflow = 'auto';
          setRunJoyride(false);
          setUserToursSeen((prev) => [...prev, path]);
        }
      }}
      continuous
      scrollOffset={0}
      disableOverlayClose
      disableScrollParentFix
      hideCloseButton
      locale={{
        skip: <strong>{t('skip')}</strong>,
        next: t('dashboardGuideNextButton'),
        back: t('dashboardGuideBackButton'),
        last: (
          <span aria-label={t('dashboardGuideLastButtonAria')}>
            {t('dashboardGuideLastButton')}
          </span>
        ),
        close: t('dashboardGuideCloseButton'),
        open: t('dashboardGuideOpenButton'),
      }}
      run={runJoyride}
      showSkipButton
      steps={steps}
      showProgress={true}
      styles={{
        options: {
          zIndex: 9000,
          primaryColor: '#000',
        },
      }}
      {...joyrideProps}
    />
  );
}

UserTour.propTypes = {
  isIntroTour: PropTypes.bool,
  joyrideProps: PropTypes.object,
  steps: PropTypes.array.isRequired,
};

export default UserTour;
